import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useMediaQuery } from "react-responsive";
import Dropdown from "react-dropdown-select";
import ReactQuill from "react-quill";
import {
  NO_PREFERENCE,
  ALLOWED_GENDERS,
  validateSupportJobLocationDetails,
  customSelectStyles2,
  alignedContentRenderer,
  customItemRenderer,
} from "../../../util_functions/helpers";
import { useJobCreate } from "../../../context/JobCreateContext";

const SocialCommunityDetailsSupportComponent = ({
  onUpdate,
  isSupportLocationError,
  clearError,
}) => {
  const navigate = useNavigate();

  const { fetchJobSelectionsById, getJobCreateIdFromCookies } = useJobCreate();

  const genderOptions = [
    { label: "", value: "", disabled: true },
    ...ALLOWED_GENDERS.map(v => ({ label: v, value: v })),
  ];


  const isMobile = useMediaQuery({ query: "(max-width: 768px)" });

  const defaultCommunityDetailsData = {
    supportJobLocation: "",
    preferredQualities: "",
    preferredGender: NO_PREFERENCE,
  };

  const [socialCommunityDetailsData, setSocialCommunityDetailsData] = useState(
    defaultCommunityDetailsData
  );

  const [computedErrors, setComputedErrors] = useState([]);
  const [visibleErrors, setVisibleErrors] = useState([]);
  const hasErrorForField = (fieldName) =>
    visibleErrors.some((e) => e.field === fieldName);

  const scdcContainerRef = useRef(null);

  useEffect(() => {
    if (isSupportLocationError) {
      const latest = normalizeLocationErrors(
        validateSupportJobLocationDetails(socialCommunityDetailsData)
      );
      setVisibleErrors(latest);
      smoothScrollToTop();
    } else {
      setVisibleErrors([]);
    }
  }, [isSupportLocationError]);


  useEffect(() => {
    const jobCreateId = getJobCreateIdFromCookies();
    if (!jobCreateId) {
      navigate("/account/fs-dashboard/my-jobs/new/standard");
    } else {
      fetchJobSelectionsById(jobCreateId)
        .then((fetchedSelections) => {
          const location = fetchedSelections?.supportJobLocationDetails || {};

          const safeData = {
            supportJobLocation: location.supportJobLocation ?? "",
            preferredQualities: location.preferredQualities ?? "",
            preferredGender: location.preferredGender ?? NO_PREFERENCE,
          };

          setSocialCommunityDetailsData(safeData);
        })
        .catch((error) => {
          console.error(
            "[SocialCommunityDetailsSupportComponent] Fetch Error:",
            error
          );
        });
    }
  }, [getJobCreateIdFromCookies, fetchJobSelectionsById, navigate]);

  const normalizeLocationErrors = (errs = []) =>
    errs.map((e) => {
      if (typeof e === "string") {
        if (/location|place/i.test(e)) return { field: "supportJobLocation", message: e };
        if (/gender/i.test(e)) return { field: "preferredGender", message: e };
        return { field: "form", message: e };
      }
      return e;
    });

  const smoothScrollToTop = () => {
    const container = scdcContainerRef.current;
    if (!container) return;

    const scrollDuration = 600;
    const scrollStep = container.scrollTop / (scrollDuration / 15);

    const scrollInterval = setInterval(() => {
      if (container.scrollTop > 0) {
        container.scrollTop -= scrollStep;
      } else {
        clearInterval(scrollInterval);
      }
    }, 15);
  };

  const notifyUpdate = (newValues) => {
    const updated = { ...socialCommunityDetailsData, ...newValues };
    const normalized = normalizeLocationErrors(
      validateSupportJobLocationDetails(updated)
    );

    setComputedErrors(normalized);
    setSocialCommunityDetailsData(updated);
    onUpdate({ data: updated, errors: normalized, isValid: normalized.length === 0 });

    if (isSupportLocationError) setVisibleErrors(normalized);
    else setVisibleErrors([]);
  };

  const handleChange = (e) => {
    clearError();
    const { name, value } = e.target;
    console.log("Changing state for:", name, "to:", value);
    notifyUpdate({ [name]: value });
  };

  const handleGenderChange = (items) => {
    clearError();
    const selected = items?.[0]?.value ?? NO_PREFERENCE;
    notifyUpdate({ preferredGender: selected });
  };

  const currentGenderValue = React.useMemo(() => {
    const match = genderOptions.find(o => o.value === socialCommunityDetailsData.preferredGender)
      || { label: NO_PREFERENCE, value: NO_PREFERENCE };
    return [match];
  }, [socialCommunityDetailsData.preferredGender, genderOptions]);

  return (
    <div className="social-community-support">
      <div
        className="social-community-support-scrollable"
        ref={scdcContainerRef}
      >
        {isSupportLocationError && visibleErrors.length > 0 && (
          <div className="scdc-error-message">
            <ul>{visibleErrors.map((err, idx) => <li key={idx}>{err.message}</li>)}</ul>
          </div>
        )}
        <h4>Support Location</h4>
        <div
          className={`scdc-radio-container-group ${hasErrorForField("supportJobLocation") ? "error-highlight" : ""
            }`}
        >
          {[
            "At home",
            "Within the community",
            "At home and within the community",
            "I’m not sure ( Discuss with worker )",
          ].map((option, i) => (
            <div className="scdc-radio-container" key={i}>
              <div className="radio-label-container">
                <input
                  type="radio"
                  name="supportJobLocation"
                  id={`supportJobLocation-${i}`}
                  value={option}
                  checked={socialCommunityDetailsData.supportJobLocation === option}
                  onChange={handleChange}
                />
                <label htmlFor={`supportJobLocation-${i}`}>{option}</label>
              </div>
            </div>
          ))}
        </div>

        <div
          className={`scdc-preferred-gender ${hasErrorForField("preferredGender") ? "error-highlight" : ""
            }`}
        >
          <label>
            <h4>Preferred Gender</h4>

            <Dropdown
              options={ALLOWED_GENDERS.map(v => ({ label: v, value: v }))}
              values={currentGenderValue}
              onChange={handleGenderChange}
              style={customSelectStyles2(isMobile)}
              contentRenderer={alignedContentRenderer}
              itemRenderer={customItemRenderer}
              clearOnSelect
              dropdownGap={4}
              closeOnScroll
            />

          </label>
        </div>
      </div>
    </div>
  );
};

export default SocialCommunityDetailsSupportComponent;
